import React, { useState } from "react";
import { useQueryState, parseAsInteger } from "nuqs";
import { Button, Popconfirm } from "antd";
import { KeyRound } from "lucide-react";
import { IArtist } from "../../../types/api/artist";
import {
  useArtistList,
  useArtistDelete,
  useBulkInviteLinks,
} from "../../../hooks/api/artist/hooks";
import { IBulkInviteLink, IBulkInviteResult } from "../../../hooks/api/artist/callbacks";
import { ArtistPanel } from "./ArtistPanel";
import ArtistModal from "./ArtistModal";

import { useMessageApi } from "../../../provider/AntdNotification";

function downloadInviteLinksCsv(links: IBulkInviteLink[]) {
  const header = "Name,Email,Set Password Link\n";
  const escape = (v: string) => `"${String(v || "").replace(/"/g, '""')}"`;
  const body = links
    .map((l) => [escape(l.name), escape(l.email), escape(l.link)].join(","))
    .join("\n");
  const blob = new Blob([header + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `artist-invite-links-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const ArtistList: React.FC = () => {
  const messageApi = useMessageApi();
  const [search, setSearch] = useQueryState("search", { defaultValue: "" });
  const [statusFilter, setStatusFilter] = useQueryState("status", { defaultValue: "" });
  const [sortBy, setSortBy] = useQueryState("sortBy", { defaultValue: "" });
  const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(1));
  const [limit, setLimit] = useQueryState("limit", parseAsInteger.withDefault(10));

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState<IArtist | null>(null);

  const queryData = {
    page,
    limit,
    search: search || undefined,
    status: statusFilter || undefined,
    sortBy: sortBy || undefined,
  };

  const { data, isLoading, isError } = useArtistList(queryData);

  const deleteMutation = useArtistDelete({
    qData: queryData,
    onSuccess: () => messageApi.success("Artist deleted successfully"),
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to delete artist"),
  });

  const handleAdd = () => {
    setSelectedArtist(null);
    setModalOpen(true);
  };

  const handleEdit = (record: IArtist) => {
    setSelectedArtist(record);
    setModalOpen(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const bulkInviteMutation = useBulkInviteLinks({
    onSuccess: (result: IBulkInviteResult, message: string) => {
      const links = result.invites || [];
      if (links.length === 0) {
        messageApi.info("Every artist already has a password set - nothing to generate.");
        return;
      }
      // CSV stays useful as the manual fallback for whatever didn't get
      // emailed (or all of it, until SES is actually live - see message).
      downloadInviteLinksCsv(links);
      messageApi.success(
        message ||
          `Generated ${links.length} invite link(s), emailed ${result.sent}, ${result.failed?.length || 0} failed - CSV downloaded.`,
      );
    },
    onError: (err: any) =>
      messageApi.error(
        err?.response?.data?.message || err?.message || "Failed to generate invite links",
      ),
  });

  return (
    <div className="w-full">
      <ArtistPanel
        title={<h1 className="text-xl font-semibold text-slate-800">Artists</h1>}
        addLabel="Add Artist"
        list={data?.data || []}
        total={data?.extra?.total || 0}
        loading={isLoading}
        isError={isError}
        queryData={queryData}
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        sortBy={sortBy}
        onSortChange={setSortBy}
        onPageChange={(newPage, newLimit) => {
          if (newLimit !== limit) {
            setLimit(newLimit);
            setPage(1);
          } else {
            setPage(newPage);
          }
        }}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        extraHeaderActions={
          <Popconfirm
            title="Generate password-set links for every artist without one?"
            description="This does not send anything - it only creates the links and downloads them as a CSV. Nothing goes out to artists until you send it yourself."
            onConfirm={() => bulkInviteMutation.mutate()}
            okText="Generate"
            cancelText="Cancel"
          >
            <Button
              size="small"
              icon={<KeyRound size={14} />}
              loading={bulkInviteMutation.isPending}
              className="flex items-center gap-2"
            >
              Generate Password-Set Links
            </Button>
          </Popconfirm>
        }
      />

      <ArtistModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedItem={selectedArtist}
        qData={queryData}
      />
    </div>
  );
};

export default ArtistList;
